/* eslint-disable camelcase */
/* istanbul ignore file reason: Tracking signatures */
import {
  ProgrammaticAdviceRecoLevel,
  type ProgrammaticAdviceRecoProduct,
} from '@/digitalAdvice/Recommendation/ProgrammaticAdviceReco.model';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';

const LEVEL_TO_INDEX: Record<ProgrammaticAdviceRecoLevel, number> = {
  [ProgrammaticAdviceRecoLevel.BASIC]: 1,
  [ProgrammaticAdviceRecoLevel.RECOMMENDED]: 3,
  [ProgrammaticAdviceRecoLevel.STANDARD]: 2,
};

export class Tracking {
  static onClickProduct(
    categoryName: string | undefined,
    level: ProgrammaticAdviceRecoLevel,
    product: ProgrammaticAdviceRecoProduct,
  ): void {
    Gtm.push({
      amp_event_name: 'Click on product card',
      block_clicked: 'Programmatic advice recommendation',
      currency: `${product.price.currency}`,
      e_action: 'product click',
      e_category: 'ecommerce',
      event: 'interaction_product click',
      event_trigger: 'Programmatic advice recommendation',
      e_label: `${product.title}`,
      playlist_name: 'Programmatic advice recommendation',
      playlist_reconciliation: `PAR-${level}-listing`,
      pr_products: [
        {
          list_name: 'Programmatic advice recommendation',
          ...(product.price.retail && {
            pr_base_price: product.price.retail,
          }),
          pr_brand: product.brand.name,
          pr_brand_id: product.brand.id,
          pr_card_name: `${level}`,
          pr_category_name: categoryName,
          pr_model_id: product.modelId,
          pr_name: product.title,
          pr_position: LEVEL_TO_INDEX[level],
          pr_price: product.price.current,
          pr_rating: product.rating.average,
          pr_rating_count: product.rating.total,
          pr_stock: product.totalStock,
        },
      ],
    });
  }

  static onDisplay(
    categoryName: string | undefined,
    products: [ProgrammaticAdviceRecoLevel, ProgrammaticAdviceRecoProduct][],
    playlistReconciliation: string,
  ): void {
    const productsTrackingData = products.map(([level, product]) => ({
      list_name: 'Programmatic advice recommendation',
      pr_article_id: product.articleId,
      ...(product.price.retail && {
        pr_base_price: product.price.retail,
      }),
      pr_brand: product.brand.name,
      pr_brand_id: product.brand.id,
      pr_card_name: `${level}`,
      pr_model_id: product.modelId,
      pr_name: product.title,
      pr_price: product.price.current,
      pr_id: product.productId,
      pr_rating: product.rating.average,
      pr_rating_count: product.rating.total,
      pr_stock: product.totalStock,
      pr_position: LEVEL_TO_INDEX[level],
    }));
    Gtm.push({
      amp_event_name: 'Display playlist',
      block_clicked: 'Programmatic advice recommendation',
      category_name: categoryName,
      card_number: products.length,
      currency: `${products[0][1].price.currency}`,
      event: 'interaction_display playlist',
      e_action: 'Display playlist',
      e_category: 'ecommerce',
      e_label: 'Programmatic advice recommendation',
      event_trigger: 'Programmatic advice recommendation',
      playlist_name: 'Programmatic advice recommendation',
      playlist_reconciliation: playlistReconciliation,
      reco_engine_name: 'PAR',
      pr_products: productsTrackingData,
    });
  }

  static onSlideChange(
    currentPosition: number,
    direction: 'left' | 'right',
    products: [ProgrammaticAdviceRecoLevel, ProgrammaticAdviceRecoProduct][],
  ): void {
    Gtm.push({
      event: 'interaction_click on navigate playlist',
      e_category: 'navigation',
      e_action: 'click on navigate playlist',
      e_label: 'Programmatic advice recommendation',
      amp_event_name: 'Click on navigate playlist',
      playlist_name: 'Programmatic advice recommendation',
      event_trigger: `${direction}`,
      card_position: currentPosition + 1,
      ...(products.at(currentPosition)?.at(0) && {
        click_element: `${products.at(currentPosition)?.at(0)}`,
      }),
    });
  }
}
